import { NavLink } from 'react-router-dom';

import { useAccountStatus } from '../../shared/hooks/useAccountStatus';
import { avatarUrl } from '../../shared/lib/avatar-storage';
import { initialsOf } from '../../shared/lib/initials';
import { useAuth } from '../AuthProvider';

import s from './SidebarAccount.module.css';

interface SidebarAccountProps {
  /** Hides the text column and leaves only the avatar, for the collapsed rail. */
  compact: boolean;
  /** The workspace name, used as the secondary line when there is no email. */
  workspace: string;
  /** Where this workspace keeps the account screen. */
  profileHref: string;
}

export function SidebarAccount({
  compact,
  workspace,
  profileHref,
}: SidebarAccountProps): JSX.Element {
  const { user } = useAuth();
  // Same query key the guards already populated, so this is a cache read on
  // every render after login — not a fifth round-trip for the shell.
  const { status } = useAccountStatus(user?.id ?? null);

  const email = user?.email?.trim() ?? '';
  // displayName is '' until the profile dialog is completed, and the email is
  // absent on a phone-only account — so both fallbacks have to survive a blank,
  // not merely an undefined. ''.split('@')[0] is '', which would otherwise slip
  // straight through a ?? and render an empty name and an empty avatar.
  const profileName = status?.displayName.trim() ?? '';
  const emailName = email.split('@')[0] ?? '';
  const name = profileName !== '' ? profileName : emailName !== '' ? emailName : 'Your account';
  const secondary = email !== '' ? email : workspace;
  const photo = avatarUrl(status?.avatarPath);

  // A link, not a div. This block has always looked like the account menu every
  // other product puts here, and until Phase 190 clicking it did nothing —
  // there was nowhere for it to go.
  return (
    <NavLink
      to={profileHref}
      viewTransition
      className={({ isActive }) => {
        const base = compact ? `${s.root ?? ''} ${s.compact ?? ''}` : (s.root ?? '');
        return isActive ? `${base} ${s.active ?? ''}` : base;
      }}
      title={compact ? name : undefined}
    >
      {photo !== null ? (
        <img className={s.photo} src={photo} alt="" width={32} height={32} decoding="async" />
      ) : (
        <span className={s.avatar} aria-hidden="true">
          {initialsOf(name)}
        </span>
      )}
      <span className={s.text}>
        <span className={s.name}>{name}</span>
        <span className={s.secondary}>{secondary}</span>
      </span>
    </NavLink>
  );
}
